import { ArrowUpRight, Copy } from "lucide-react";
import { useState } from "react";
import Reveal from "@/components/Reveal";

const EMAIL = "engage@folksfoundation.com";

const CHANNELS = [
  {
    k: "01",
    label: "Institutional & sovereign partnerships",
    addr: "institutions@folksfoundation.com",
  },
  {
    k: "02",
    label: "Vertical programs & policy collaboration",
    addr: "programs@folksfoundation.com",
  },
  {
    k: "03",
    label: "Press & long-form editorial",
    addr: "press@folksfoundation.com",
  },
];

export default function Contact() {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // noop
    }
  };

  return (
    <div data-testid="contact-page" style={{ paddingTop: 168 }}>
      {/* HERO EMAIL */}
      <section
        className="container-wide"
        style={{ paddingTop: "8rem", paddingBottom: "6rem" }}
      >
        <Reveal>
          <p className="label-mono">— Contact / Engage the foundation</p>
        </Reveal>

        <Reveal delay={0.1}>
          <a
            href={`mailto:${EMAIL}`}
            data-testid="contact-email-hero"
            className="block mt-10"
            style={{
              fontFamily: "Fraunces, Cormorant Garamond, serif",
              fontVariationSettings: '"opsz" 144, "SOFT" 0, "WONK" 1',
              fontWeight: 600,
              fontSize: "clamp(2rem, 8vw, 8rem)",
              letterSpacing: "-0.04em",
              lineHeight: 0.92,
              color: "var(--charcoal)",
              transition: "color 0.5s cubic-bezier(0.16,1,0.3,1)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--mulberry)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--charcoal)")}
          >
            engage@
            <br />
            folksfoundation.com
          </a>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-cta-email"
              className="btn-mulberry"
            >
              Open mail client <ArrowUpRight size={14} />
            </a>
            <button
              data-testid="contact-cta-copy"
              onClick={copyEmail}
              className="btn-ghost-dark"
            >
              {copied ? "Copied" : "Copy email"} <Copy size={14} />
            </button>
          </div>
        </Reveal>
      </section>

      {/* CHANNELS */}
      <section
        data-testid="contact-channels"
        className="container-wide"
        style={{ paddingBottom: "6rem" }}
      >
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <p className="label-mono md:col-span-3">— Channels</p>
          <h2
            className="md:col-span-9 font-display font-bold"
            style={{
              fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
              letterSpacing: "-0.035em",
              lineHeight: 0.95,
            }}
          >
            Three lanes of engagement.
            <br />
            <span style={{ color: "var(--mulberry)" }}>
              Choose the one closest to your institution.
            </span>
          </h2>
        </div>

        <div className="divider-line">
          {CHANNELS.map((c) => (
            <Reveal key={c.k}>
              <a
                href={`mailto:${c.addr}`}
                data-testid={`channel-${c.k}`}
                className="group block border-b border-[color:var(--soft-border)] py-8 md:py-10"
              >
                <div className="grid grid-cols-12 items-baseline gap-6">
                  <span className="pillar-num col-span-2 md:col-span-1">{c.k}</span>
                  <span
                    className="col-span-10 md:col-span-7 font-display font-bold"
                    style={{
                      fontSize: "clamp(1.25rem, 2.4vw, 2rem)",
                      letterSpacing: "-0.025em",
                      lineHeight: 1.1,
                    }}
                  >
                    {c.label}
                  </span>
                  <span
                    className="hidden md:inline col-span-3 font-mono text-xs tracking-[0.2em] uppercase"
                    style={{ color: "var(--mulberry)" }}
                  >
                    {c.addr}
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="col-span-1 justify-self-end group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                    style={{ color: "var(--mulberry)" }}
                  />
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OFFICE BAND */}
      <section
        data-testid="contact-office"
        style={{
          background: "var(--charcoal)",
          color: "var(--pearl)",
          padding: "8rem 0",
        }}
      >
        <div className="container-wide grid md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <p className="label-mono-light mb-4">— Hours</p>
            <p className="font-body text-lg leading-relaxed opacity-85">
              Mon — Thu · 09:00–18:00 CET
              <br />
              Fri · 09:00–14:00 CET
              <br />
              By appointment for institutional meetings.
            </p>
          </div>
          <div className="md:col-span-6">
            <p className="label-mono-light mb-4">— Notice</p>
            <p className="font-body text-lg leading-relaxed opacity-85">
              We do not solicit individual donations. Folks Foundation is
              structured for long-duration institutional collaboration. All
              correspondence is reviewed by the secretariat.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
